import { ReactElement, useEffect, useState } from 'react';

import { ApiRoute, FeatureFlag, Route } from 'config';
import { get, post } from 'utils/network';

import Plan from 'models/Plan';
import FetchState from 'models/FetchState';

import useAsyncEffect from 'hooks/useAsyncEffect';
import useCurrentUser from 'hooks/useCurrentUser';
import useAddresses from 'hooks/useAddresses';
import useSubscription from 'hooks/useSubscription';

import CheckoutSummary from './CheckoutSummary';
import IPlanInfo from '../../../models/IPlanInfo';
import { ICoupon } from '../../../models/Coupon';

/* eslint react-hooks/rules-of-hooks: 0 */

export type discountInfo = {
  value: number,
  couponCount: number,
  couponName: string,
  couponType: 'percentage_off' | 'dollar_off',
  couponValue: number | string,
  couponFreeShipping: boolean,
}

export type orderPricingInfo = {
  shippingAmount: number,
  shippingFree: boolean,
  extendedAreaSurcharge: string,
  totalPrice: number,
  hasGift: boolean,
  hasGiveaway: boolean
  remainingCredit: number,
  crdFee: number,
  tax: number,
  taxAfterDiscount: number,
  surchargeAfterDiscount: string,
  shippingFeeAfterDiscount: number,
  crdFeeAfterDiscount: number,
}

type Props = {
  isDrawerOpened?: boolean;
  onModalOpen?: () => void;
  onSummaryClick?: () => void;
  onClose?: () => void;
  loggedIn?: boolean
  selectedPlan: Plan
  menuShippingDate?: string,
};

type gonData = {
  featureFlags?: Record<string, boolean>,
  coupon?: ICoupon
};

function CheckoutSummaryContainer({
  isDrawerOpened,
  onModalOpen = () => null,
  onSummaryClick = () => null,
  onClose = () => null,
  loggedIn = false,
  selectedPlan,
  menuShippingDate,
}: Props): ReactElement {
  let handlePlanChange: (Plan) => void;
  let isPlanReloading: boolean;
  let submitDiscountCode: () => void;
  let menuDate: string;
  let discountDetails: discountInfo;
  let orderPricing: orderPricingInfo;
  let addressesLength;
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedServings, setSelectedServings] = useState(
    selectedPlan.servings,
  );
  const [addedDiscountCode, setAddedDiscountCode] = useState('');

  useAsyncEffect(async () => {
    const response = await get<IPlanInfo[]>(ApiRoute.PLANS_SHORT);
    // @ts-expect-error TODO: fix null safety
    Plan.initialize(response.parsedBody);
  }, []);

  const {
    isHigherConversionOnboardingEnabled,
  } = (window.gon as gonData).featureFlags
    ?? { isHigherConversionOnboardingEnabled: false };

  const {
    // not camel case as gotten from CouponSerializer data
    discount_code: discountCode,
    discount_type: discountType,
    discount_price: discountPrice,
    constraints,
    allowed_plans: allowedPlans,
    max_order_count: maxOrderCount,
  } = (window.gon as gonData).coupon ?? {
    discountCode: '',
    discountType: 'percentage_off',
    discountPrice: 0,
    constraints: {},
    allowedPlans: [],
    maxOrderCount: 0,
  };

  const [plan, setPlan] = useState(selectedPlan);
  if (loggedIn) {
    const { addresses } = useAddresses();
    addressesLength = addresses.length;

    const {
      subscription,
      reload: reloadSubscription,
      fetchState: subscriptionFetchState,
    } = useSubscription();

    const {
      currentUser,
      reload: reloadUser,
      fetchState: userFetchState,
    } = useCurrentUser();
    const { signup_summary: signupSummary } = subscription;
    menuDate = signupSummary?.delivery_date;

    discountDetails = {
      value: signupSummary?.discount?.value || 0,
      couponName: signupSummary?.discount?.coupon_name || '',
      couponValue: signupSummary?.discount?.coupon_value,
      couponType: signupSummary?.discount?.coupon_discount_type,
      couponCount: signupSummary?.discount?.coupon_count,
      couponFreeShipping: currentUser?.subscription?.coupon?.free_shipping,
    };

    let cumulativePrice = signupSummary?.box_total || 0;
    if (discountDetails.couponFreeShipping && !signupSummary?.shipping.free) {
      cumulativePrice -= 12;
    }

    orderPricing = {
      shippingAmount: 12,
      shippingFree: signupSummary?.shipping.free,
      extendedAreaSurcharge: signupSummary.shipping.shipping_surcharge,
      totalPrice: cumulativePrice,
      hasGift: !!discountDetails.value
          && signupSummary?.discount?.type === 'gift_discount',
      hasGiveaway: !!discountDetails.value
          && signupSummary?.discount?.type === 'giveaway_discount',
      remainingCredit: (currentUser.credit || 0) - discountDetails.value > 0
        ? currentUser.credit - discountDetails.value
        : 0,
      crdFee: signupSummary?.crd_fee,
      tax: signupSummary?.sales_tax || 0,
      taxAfterDiscount: Number(signupSummary?.sales_tax_after_discount) || 0,
      surchargeAfterDiscount: signupSummary.shipping.shipping_surcharge_after_discount,
      shippingFeeAfterDiscount: Number(signupSummary?.shipping.shipping_fee_after_discount) || 0,
      crdFeeAfterDiscount: signupSummary?.crd_fee_after_discount,
    };

    const [isPlanChanging, setPlanChanging] = useState(false);

    isPlanReloading = userFetchState === FetchState.RELOADING
        || subscriptionFetchState === FetchState.RELOADING
        || isPlanChanging;

    handlePlanChange = async (newPlan: Plan) => {
      setPlanChanging(true);

      await post(`${ApiRoute.USERS}/me/subscription`, {
        subscription: {
          plan: newPlan.name,
          extra_meals: newPlan.selectedExtras,
        },
      });

      reloadUser();

      setPlanChanging(false);
    };

    submitDiscountCode = async () => {
      setErrorMessage('');

      const resp = await post(ApiRoute.REDEEM_CODE, {
        code: addedDiscountCode,
      });

      setAddedDiscountCode('');

      if (resp.status === 201) {
        reloadUser();
      } else if (resp.status === 422) {
        setErrorMessage(
          (resp.parsedBody as Record<string, string>).details,
        );
      }
    };

    useEffect(() => {
      const newPlan = new Plan(subscription.plan, subscription.extras);

      setSelectedServings(newPlan.servings);
      setPlan(newPlan);
    }, [subscription, setPlan]);

    useAsyncEffect(async () => {
      reloadSubscription();
    }, [currentUser, addresses]);
  } else {
    isPlanReloading = false; // since no call being made, nothing to await or reload
    handlePlanChange = async (newPlan: Plan) => {
      const planName = newPlan ? newPlan.name : plan.name;
      const extras = newPlan ? newPlan.extras : plan.extras;
      window.location.href = `${Route.SIGNUP_CREATE_ACCOUNT}?plan=${planName}&extras=${extras}`;
    };
    submitDiscountCode = async () => {
      // do nothing as no discount codes can be submitted when user not logged in
    };

    menuDate = menuShippingDate ?? '';
    addressesLength = 0;

    discountDetails = {
      // @ts-expect-error TODO: fix null safety
      value: discountPrice,
      // @ts-expect-error TODO: fix null safety
      couponName: discountCode,
      // @ts-expect-error TODO: fix null safety
      couponValue: discountPrice,
      // @ts-expect-error TODO: fix null safety
      couponType: discountType,
      // @ts-expect-error TODO: fix null safety
      couponCount: maxOrderCount,
      couponFreeShipping: false,
    };

    let discountAmount = (discountDetails.value ?? 0);
    if (discountDetails.couponType === 'percentage_off') {
      discountAmount = (discountAmount / 100) * plan.planPrice;

      if (constraints.max_amount) {
        discountAmount = Math.min(Number(constraints.max_amount), discountAmount);
      }
    }

    const totalCost = plan.planPrice + (plan.planPrice >= 100 ? 0 : 12) - discountAmount;

    orderPricing = {
      shippingAmount: 12,
      shippingFree: plan.planPrice >= 100,
      extendedAreaSurcharge: '0',
      totalPrice: totalCost,
      hasGift: false,
      hasGiveaway: false,
      remainingCredit: 0,
      crdFee: 0,
      tax: 0,
      taxAfterDiscount: 0,
      surchargeAfterDiscount: '0.0',
      shippingFeeAfterDiscount: 12,
      crdFeeAfterDiscount: 0,
    };
  }

  return (
    <>
      <CheckoutSummary
        isDrawerOpened={isDrawerOpened}
        onModalOpen={onModalOpen}
        onSummaryClick={onSummaryClick}
        onClose={onClose}
        plan={plan}
        setPlan={setPlan}
        handlePlanChange={handlePlanChange}
        submitDiscountCode={submitDiscountCode}
        isPlanReloading={isPlanReloading}
        menuDate={menuDate}
        discount={discountDetails}
        orderPricing={orderPricing}
        addressesLength={addressesLength}
        higherConversionOnboardingEnabled={isHigherConversionOnboardingEnabled}
        errorMessage={errorMessage}
        discountCode={addedDiscountCode}
        setDiscountCode={setAddedDiscountCode}
        selectedServings={selectedServings}
        setSelectedServings={setSelectedServings}
        loggedIn={loggedIn}
      />
    </>
  );
}
export default CheckoutSummaryContainer;
